import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { activityService, communityService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';

const COMMUNITY_ICONS = ['school', 'palette', 'account_balance', 'groups', 'park', 'work'];

function parseActivityTimestamp(timestamp) {
  if (!timestamp) return null;
  if (Array.isArray(timestamp)) {
    const [y, mo, d, h = 0, m = 0, s = 0] = timestamp;
    return new Date(y, mo - 1, d, h, m, s);
  }
  return new Date(timestamp);
}

function formatActivityTime(timestamp) {
  const date = parseActivityTimestamp(timestamp);
  if (!date || isNaN(date.getTime())) return '';
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return null; // sentinel for "just now" — resolved with t() in component
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  return `${Math.floor(diffH / 24)}d`;
}

export default function ActivityDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: communities = [] } = useQuery({
    queryKey: ['userCommunities'],
    queryFn: async () => { const r = await communityService.getUserCommunities(); return r.data; },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });


  const { data: recentActivity = [] } = useQuery({
    queryKey: ['recentActivity'],
    queryFn: async () => {
      const r = await activityService.getRecentActivity(5);
      return (r.data || []).slice(0, 5);
    },
    enabled: !!user,
  });

  const goTo = (path) => { onClose(); navigate(path); };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/30"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-[71] flex flex-col overflow-y-auto transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ backgroundColor: 'var(--chrome-sidebar-bg)', borderLeft: '1px solid var(--chrome-border-subtle)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: '1px solid var(--chrome-border-divider)' }}>
          <span className="text-sm font-bold" style={{ color: 'var(--chrome-strong)' }}>{t('Actividad')}</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--chrome-hover-bg)] transition-colors"
            style={{ color: 'var(--chrome-muted)' }}
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-8">
          {/* Active Circles */}
          {communities.length > 0 && (
            <section>
              <h4
                className="text-[10px] font-bold uppercase tracking-widest mb-4 flex items-center justify-between"
                style={{ color: 'var(--chrome-label)' }}
              >
                {t('Comunidades')}
                <span
                  onClick={() => goTo('/communities')}
                  className="material-symbols-outlined text-sm cursor-pointer"
                  style={{ color: 'var(--chrome-label)' }}
                >
                  more_horiz
                </span>
              </h4>
              <div className="space-y-3">
                {communities.slice(0, 4).map((c, i) => {
                  const bgColors = ['bg-primary-container', 'bg-secondary-container', 'bg-primary-container/40', 'bg-secondary-container/40'];
                  const textColors = ['text-on-primary-container', 'text-on-secondary-container', 'text-on-primary-container', 'text-on-secondary-container'];
                  return (
                    <div
                      key={c.id}
                      onClick={() => goTo(`/communities/${c.id}`)}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className={`w-9 h-9 rounded-xl ${c.imageId ? 'bg-surface' : bgColors[i % 4]} flex items-center justify-center ${textColors[i % 4]} shrink-0 overflow-hidden`}>
                        {c.imageId
                          ? <img alt={c.name} className="w-full h-full object-cover" src={getImageUrl(c.imageId)} />
                          : <span className="material-symbols-outlined text-[18px]">{COMMUNITY_ICONS[i % COMMUNITY_ICONS.length]}</span>
                        }
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors" style={{ color: 'var(--chrome-strong)' }}>{c.name}</p>
                        <p className="text-[10px]" style={{ color: 'var(--chrome-label)' }}>{t('{{count}} miembros', { count: c.memberCount })}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <section>
              <h4 className="text-[10px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--chrome-label)' }}>
                {t('Actividad reciente')}
              </h4>
              <div className="space-y-5">
                {recentActivity.map((event, i) => {
                  const isLast = i === recentActivity.length - 1;
                  const isMe = user && event.actorId === user.id;
                  const displayName = isMe ? t('Vos') : (event.actorName || '?');
                  const initial = (event.actorName || '?').charAt(0).toUpperCase();

                  let actionText;
                  if (event.type === 'NEW_PRODUCT') {
                    actionText = <> {t('publicó')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.productName}</span>{event.communityName && <> {t('en')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.communityName}</span></>}</>;
                  } else if (event.type === 'MEMBER_JOINED') {
                    actionText = <> {t('se unió a')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.communityName}</span></>;
                  } else if (event.type === 'RATING_RECEIVED') {
                    actionText = <> {t('te calificó')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.rating}★</span>{event.productName && <> {t('por')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.productName}</span></>}</>;
                  }

                  return (
                    <div key={`${event.actorId}-${event.timestamp}`} className="relative flex gap-3">
                      {!isLast && <div className="absolute left-[13px] top-7 -bottom-5 w-0.5" style={{ backgroundColor: 'var(--chrome-border-divider)' }} />}
                      <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: 'var(--chrome-avatar-accent)' }}>
                        {initial}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-xs leading-snug" style={{ color: 'var(--chrome-body)' }}>
                          <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{displayName}</span>{actionText}
                        </p>
                        <span className="text-[9px] font-bold tracking-wider mt-1 block uppercase" style={{ color: 'var(--chrome-label)' }}>
                          {(() => {
                            const val = formatActivityTime(event.timestamp);
                            if (val === null) return t('justo ahora');
                            if (/^\d+m$/.test(val)) return t('{{count}}min', { count: parseInt(val) });
                            if (/^\d+h$/.test(val)) return t('{{count}}h', { count: parseInt(val) });
                            if (/^\d+d$/.test(val)) return t('{{count}}d', { count: parseInt(val) });
                            return val;
                          })()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {communities.length === 0 && recentActivity.length === 0 && (
            <p className="text-sm text-center pt-8" style={{ color: 'var(--chrome-muted)' }}>{t('Sin actividad aún')}</p>
          )}
        </div>
      </div>
    </>
  );
}
